import { useRef, useState } from 'react'
import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Alert, App, Button, Modal, Tabs, Tag } from 'antd'
import type { OrderGroup, RentalOrder, ServiceOrder } from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { useOrderGroupMutation } from './hooks/use-order-group-mutation.ts'

type SelectedOrder = Pick<RentalOrder | ServiceOrder, 'id' | 'orderNo' | 'orderGroupId' | 'orderGroupName'>

export function OrderGroupIncludeModal({ group, onClose }: { group: OrderGroup; onClose: () => void }) {
  const { message, modal } = App.useApp()
  const mutation = useOrderGroupMutation()
  const [open, setOpen] = useState(true)
  const [rentalOrders, setRentalOrders] = useState<SelectedOrder[]>([])
  const [serviceOrders, setServiceOrders] = useState<SelectedOrder[]>([])
  const rentalIds = rentalOrders.map((order) => order.id)
  const serviceIds = serviceOrders.map((order) => order.id)
  const submission = useRef<{ payload: string; key: string } | undefined>(undefined)
  const count = rentalIds.length + serviceIds.length
  const save = async () => {
    const payload = JSON.stringify({ rentalIds, serviceIds })
    if (submission.current?.payload !== payload) {
      submission.current = { payload, key: crypto.randomUUID() }
    }
    try {
      await mutation.mutateAsync({
        orderGroupId: group.id,
        expectedCustomerId: group.customerId,
        expectedCount: group.orderCount,
        idempotencyKey: submission.current.key,
        change: { kind: 'include', rentalIds, serviceIds }
      })
      void message.success(`已纳入 ${count} 个订单`)
      setOpen(false)
    } catch (error) {
      void message.error(getErrorMessage(error, '纳入订单组失败'))
    }
  }
  const submit = () => {
    if (count === 0 || mutation.isPending) return
    const movingOrders = [...rentalOrders, ...serviceOrders].filter(
      (order) => order.orderGroupId !== null && order.orderGroupId !== group.id
    )
    if (movingOrders.length === 0) {
      void save()
      return
    }
    modal.confirm({
      title: '确认强制移入订单组？',
      width: '40rem',
      content: (
        <div className="space-y-3">
          <p>
            已选订单中有 {movingOrders.length} 个属于其他订单组。继续后，这些订单将从原组移出，并强制移入「
            {group.name}」。每个订单只能归属一个订单组。
          </p>
          <ul className="space-y-2">
            {movingOrders.map((order) => (
              <li key={order.orderNo}>
                <span className="font-medium">{order.orderNo}</span>
                <span>：{order.orderGroupName || `订单组 #${order.orderGroupId}`}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: '确认移入',
      cancelText: '返回检查',
      focusable: { autoFocusButton: 'cancel' },
      onOk: save
    })
  }
  return (
    <Modal
      classNames={{ container: 'h-[80vh]' }}
      open={open}
      // 页面在 onClose 中卸载组件，必须等退出动画结束后再通知页面。
      afterClose={onClose}
      width="72rem"
      title={`纳入订单组 · ${group.name}`}
      onCancel={() => setOpen(false)}
      onOk={submit}
      okText={`纳入已选订单（${count}）`}
      cancelText="取消"
      confirmLoading={mutation.isPending}
      okButtonProps={{ disabled: count === 0 || mutation.isPending }}
      cancelButtonProps={{ disabled: mutation.isPending }}
      closable={!mutation.isPending}
      keyboard={!mutation.isPending}
      mask={{ closable: false }}
    >
      <Alert
        showIcon
        type="info"
        className="mb-4"
        title={`仅可选择客户「${group.customerName}」的订单`}
        description="可跨页多选，每类最多选择 100 个订单。已属于其他订单组的订单需二次确认后强制移入当前组；当前组内的订单不可重复选择。"
      />
      <Tabs
        items={[
          {
            key: 'rental',
            label: `租赁订单（已选 ${rentalIds.length}）`,
            children: (
              <OrderSelectionTable
                group={group}
                kind="rental"
                selectedIds={rentalIds}
                onSelect={setRentalOrders}
                disabled={mutation.isPending}
              />
            )
          },
          {
            key: 'service',
            label: `服务订单（已选 ${serviceIds.length}）`,
            children: (
              <OrderSelectionTable
                group={group}
                kind="service"
                selectedIds={serviceIds}
                onSelect={setServiceOrders}
                disabled={mutation.isPending}
              />
            )
          }
        ]}
      />
    </Modal>
  )
}

function OrderSelectionTable({
  group,
  kind,
  selectedIds,
  onSelect,
  disabled
}: {
  group: OrderGroup
  kind: 'rental' | 'service'
  selectedIds: number[]
  onSelect: (orders: SelectedOrder[]) => void
  disabled: boolean
}) {
  const trpc = useTRPC()
  const [search, setSearch] = useState<{
    current: number
    pageSize: number
    keyword?: string
    sortField: 'createdAt'
    sortOrder: 'asc' | 'desc'
  }>({ current: 1, pageSize: 10, sortField: 'createdAt', sortOrder: 'desc' })
  const input = { ...search, customerId: group.customerId, archiveStatus: 'all' as const }
  const rentals = useQuery({
    ...trpc.order.rental.list.queryOptions(input),
    enabled: kind === 'rental',
    placeholderData: keepPreviousData
  })
  const services = useQuery({
    ...trpc.order.service.list.queryOptions(input),
    enabled: kind === 'service',
    placeholderData: keepPreviousData
  })
  const query = kind === 'rental' ? rentals : services
  type Row = RentalOrder | ServiceOrder
  const columns: ProColumns<Row>[] = [
    {
      title: '关键词 / ID',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: '订单号、ID 或备注', allowClear: true }
    },
    { title: 'ID', dataIndex: 'id', copyable: true, search: false, width: '5rem' },
    { title: '订单号', dataIndex: 'orderNo', search: false, width: '14rem' },
    { title: '业务', dataIndex: 'businessName', search: false, ellipsis: true },
    { title: '服务对象', dataIndex: 'subjectName', search: false, ellipsis: true },
    {
      title: '当前订单组',
      dataIndex: 'orderGroupName',
      search: false,
      render: (_, row) => (row.orderGroupId === group.id ? <Tag>已在当前组</Tag> : row.orderGroupName || '未分组')
    },
    { title: '创建人', dataIndex: ['creator', 'name'], search: false },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      search: false,
      valueType: 'dateTime',
      sorter: true,
      sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend',
      width: '11rem'
    }
  ]
  return (
    <>
      {query.isError && (
        <Alert
          type="error"
          showIcon
          title={getErrorMessage(query.error, '订单加载失败')}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      <ProTable<Row>
        rowKey="id"
        size="small"
        columns={columns}
        form={{ name: `order-group-include-${kind}` }}
        dataSource={query.data?.data ?? []}
        columnsState={{ persistenceKey: `order-group-include-${kind}:table`, persistenceType: 'localStorage' }}
        loading={query.isFetching}
        toolBarRender={false}
        search={{ labelWidth: 'auto', defaultCollapsed: false }}
        scroll={{ x: '65rem' }}
        onSubmit={(values) =>
          setSearch({
            ...search,
            current: 1,
            keyword: typeof values.keyword === 'string' ? values.keyword.trim() || undefined : undefined
          })
        }
        onReset={() => setSearch({ current: 1, pageSize: 10, sortField: 'createdAt', sortOrder: 'desc' })}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action === 'sort') {
              const sort = Array.isArray(sorter) ? sorter[0] : sorter
              setSearch({ ...search, current: 1, sortOrder: sort.order === 'ascend' ? 'asc' : 'desc' })
            }
          }
          /* eslint-enable max-params */
        }
        pagination={{
          current: search.current,
          pageSize: search.pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          onChange: (current, pageSize) => setSearch({ ...search, current, pageSize })
        }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          preserveSelectedRowKeys: true,
          onChange: (keys, selectedRows) => {
            if (keys.length <= 100) {
              // preserveSelectedRowKeys 同时保留跨页记录，供提交前展示原订单组。
              onSelect(
                selectedRows.map(({ id, orderNo, orderGroupId, orderGroupName }) => ({
                  id,
                  orderNo,
                  orderGroupId,
                  orderGroupName
                }))
              )
            }
          },
          getCheckboxProps: (row) => ({
            disabled:
              disabled ||
              query.isFetching ||
              row.orderGroupId === group.id ||
              (selectedIds.length >= 100 && !selectedIds.includes(row.id))
          })
        }}
        tableAlertOptionRender={() => (
          <Button type="link" disabled={disabled} onClick={() => onSelect([])}>
            清空已选
          </Button>
        )}
      />
    </>
  )
}
